import { Flex } from '@grupoboticario/flora-react';
import {
  Carousel,
  CarouselContent,
  NextButton,
  PreviousButton,
} from '@grupoboticario/flora-team-react-carousel';
import { Link } from 'react-router-dom';
import slugify from 'slugify';

import { Button, HeadingWithButton, ResponsiveFloraTypography } from 'components';
import { SLIDES_TO_SHOW } from 'config/environments/shared';
import { ListToShare } from 'domains/images-to-share';
import { isMobile as isMobileHelper } from 'helpers';
import { useTracking } from 'hooks';

import { CarouselToShareProps } from './CarouselToShare.d';
import Styles from './CarouselToShare.styles';
import { ModalShare, InfoOnboarding } from '../../components';

export const CarouselToShare = ({ data }: { data: CarouselToShareProps }) => {
  const { listToShare } = data;
  const isMobile = isMobileHelper();
  const maxItemsOnCarousel = 12;
  const shouldRenderLinkOnLastItem = listToShare && listToShare.length > maxItemsOnCarousel;
  const filteredItems = listToShare
    ? listToShare
        .slice(0, maxItemsOnCarousel)
        .filter((list) => Object.keys(list).find((key) => key === 'image'))
    : [];
  const lastItem = filteredItems[filteredItems.length - 1];
  const { sendDataToGTM } = useTracking();

  const handleCarouselInteraction = (event: React.MouseEvent<HTMLButtonElement> | number) => {
    const interactions: { [key: string]: string } = {
      prev: 'click:anterior',
      next: 'click:proximo',
    };
    const analyticsData = {
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_img_divulgacao',
        cd_section: slugify(data.title || '', { lower: true }),
      },
    };

    if (typeof event === 'number') {
      const index = event as number;

      sendDataToGTM({
        ...analyticsData,
        event_parameters: {
          ...analyticsData.event_parameters,
          cd_interaction_detail: `click:carrossel:${index + 1}`,
        },
      });

      return;
    }

    sendDataToGTM({
      ...analyticsData,
      event_parameters: {
        ...analyticsData.event_parameters,
        cd_interaction_detail: interactions[event.currentTarget.dataset.interaction || ''],
      },
    });
  };

  const handleDownloadAll = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_img_divulgacao',
        cd_interaction_detail: 'click:baixar-todas-as-imagens',
        cd_quantity: filteredItems.length.toString(),
        cd_section: slugify(data.title || '', { lower: true }),
      },
    });
  };

  const handleLastItemInteraction = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_img_divulgacao',
        cd_interaction_detail: 'click:ver-tudo:nav',
        cd_section: slugify(`${data.title || ''}`, { lower: true }),
      },
    });
  };

  const handleModal = ({ item, indice }: { item: ListToShare; indice: number }) => {
    sendDataToGTM({
      event: 'select_promotion',
      event_parameters: {
        cd_interaction_detail: 'click:imagem',
        cd_section: slugify(`${data.title || ''}`, { lower: true }),
      },
      ecommerce: {
        creative_name: item?.image?.url,
        creative_slot: indice,
        promotion_id: slugify(
          `${data.title}-${item?.image?.alt || item?.description || ''}-${indice}`,
          {
            lower: true,
          }
        ),
        promotion_name: slugify(item?.image?.alt || item?.description || data.title || '', {
          lower: true,
        }),
      },
    });
  };

  return (
    <Flex direction='column' gap='$3'>
      <HeadingWithButton title={data.title || ''} />

      <ResponsiveFloraTypography fontSize='bodyLargeShort' fontWeight='medium'>
        {data.description}
      </ResponsiveFloraTypography>

      <Flex justify='flex-start'>
        <Button
          variation='DownloadAll'
          variantProps={{
            items: data.listToShare?.map((item) => item.image) || [],
            onClick: handleDownloadAll,
            title: data.title || '',
          }}
        />
      </Flex>
      <InfoOnboarding />

      {filteredItems.length <= SLIDES_TO_SHOW[isMobile ? 'mobile' : 'desktop'] ? (
        <Styles.WithoutCarouselWrapper>
          {filteredItems.map((item, index) => (
            <ModalShare
              data={data}
              imageItem={item}
              indice={index + 1}
              key={`carousel-item-${index}`}
            >
              <Styles.Image
                src={item.image.url}
                alt={item.image.alt || item.description}
                data-isolated
                onClick={() => handleModal({ item, indice: index + 1 })}
                data-testid='carousel-image'
              />
            </ModalShare>
          ))}
        </Styles.WithoutCarouselWrapper>
      ) : (
        <Styles.CarouselWrapper>
          <Carousel onDotPress={handleCarouselInteraction}>
            <PreviousButton onClick={handleCarouselInteraction} data-interaction='prev' />

            <CarouselContent
              slidesToShow={SLIDES_TO_SHOW[isMobile ? 'mobile' : 'desktop']}
              infinite={false}
            >
              {filteredItems.map((item, index) => (
                <ModalShare
                  key={`carousel-item-${index}`}
                  data={data}
                  indice={index + 1}
                  imageItem={item}
                >
                  <Styles.Image
                    src={item.image.url}
                    alt={item.image.alt || item.description}
                    onClick={() => handleModal({ item, indice: index + 1 })}
                    data-testid='carousel-image'
                  />
                </ModalShare>
              ))}

              {shouldRenderLinkOnLastItem && (
                <>
                  <Flex align='center' justify='center'>
                    <Styles.LastItemButton
                      size='small'
                      aria-label={`ver tudo de ${data.title || ''}`}
                      role='button'
                      onClick={handleLastItemInteraction}
                    >
                      <Link to={encodeURIComponent(data.title || '')}>
                        <ResponsiveFloraTypography
                          fontSize='bodySmallShort'
                          color='$backgroundPrimary'
                          fontWeight='medium'
                        >
                          Ver tudo
                        </ResponsiveFloraTypography>
                      </Link>
                    </Styles.LastItemButton>

                    <Styles.LastItemImage src={lastItem.image.url} alt={lastItem.image.alt} />
                  </Flex>
                </>
              )}
            </CarouselContent>

            <NextButton onClick={handleCarouselInteraction} data-interaction='next' />
          </Carousel>
        </Styles.CarouselWrapper>
      )}
    </Flex>
  );
};
